import React from "react";

import type {
  OutstandingImportDTO,
  OutstandingKind,
  OutstandingPushDTO,
  OutstandingRowDTO,
} from "../../data/outstandingApi";
import {
  ACTION_LABEL,
  KIND_COPY,
  ROW_STATUS_LABEL,
  hasBeenPushed,
  warningCount,
} from "../../domain/outstandingImport";
import { Callout } from "../../components/Callout";
import { cn } from "../../components/cn";

/**
 * What the sheet would do, in four numbers — and once it has been pushed, what
 * it did.
 *
 * The counts before the rows because this is the question a CA actually has —
 * "did my two hundred documents land, and did anything bounce" — and two
 * hundred rows is the wrong shape to answer it in.
 *
 * The tiles swap wholesale after a push rather than sitting alongside new ones.
 * "To be added: 12" next to "Added: 9" is two numbers about the same twelve
 * documents, and reading it wrong means believing twenty-one were involved.
 */

/**
 * Tinted only while it counts something. Four coloured boxes where three say
 * zero is noise wearing a highlighter; a quiet zero beside one loud number is
 * the actual shape of the news.
 */
const TILE_STYLE: Record<string, string> = {
  new: "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400",
  update: "border-sky-500/30 bg-sky-500/10 text-sky-700 dark:text-sky-400",
  invalid: "border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-400",
  unchanged: "border-border bg-muted text-muted-foreground",
};

function Tile({ label, count, tone }: { label: string; count: number; tone: string }) {
  return (
    <div
      className={cn(
        "flex flex-col gap-0.5 rounded-md border px-3 py-2",
        count === 0 ? "border-transparent bg-muted/40 text-muted-foreground/60" : TILE_STYLE[tone]
      )}
    >
      <span className="text-xl font-semibold tabular-nums">{count}</span>
      <span className="text-xs font-medium">{label}</span>
    </div>
  );
}

export function OutstandingImportSummary({
  summary,
  rows,
  kind,
  push,
  pushing,
}: {
  summary: OutstandingImportDTO;
  rows: OutstandingRowDTO[];
  kind: OutstandingKind;
  /** Only from a commit response — a plain read has no batch to report. */
  push?: OutstandingPushDTO | null;
  pushing?: boolean;
}) {
  const copy = KIND_COPY[kind];
  const warnings = warningCount(rows);
  const toPush = summary.new_rows + summary.changed_rows;

  if (hasBeenPushed(summary)) {
    // No sniffing of Zoho's wording here any more. A number Zoho already holds
    // comes back from the server as its own row status, so `failed` now means
    // what it says: something a person has to do something about.
    return (
      <PushOutcome
        summary={summary}
        push={push ?? null}
        pushing={pushing === true}
        toPush={toPush}
        noun={copy.noun}
        nounPlural={copy.nounPlural}
      />
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <Tile label={ACTION_LABEL.new} count={summary.new_rows} tone="new" />
        <Tile label={ACTION_LABEL.update} count={summary.changed_rows} tone="update" />
        <Tile label={ACTION_LABEL.invalid} count={summary.invalid_rows} tone="invalid" />
        <Tile label={ACTION_LABEL.unchanged} count={summary.unchanged_rows} tone="unchanged" />
      </div>

      {/*
        The one number that matters for cost: unchanged rows are never sent, and
        each document sent costs two Zoho calls — the create, then the status
        call that makes the draft real.
      */}
      <p className="text-sm text-muted-foreground">
        {toPush === 0
          ? "Nothing in this sheet would change anything in Zoho."
          : `${toPush} ${toPush === 1 ? copy.noun : copy.nounPlural} would be sent to Zoho. The other ${
              summary.total_rows - toPush
            } cost nothing.`}
      </p>

      {summary.invalid_rows > 0 && (
        <Callout variant="warning">
          {summary.invalid_rows} {summary.invalid_rows === 1 ? "row needs" : "rows need"} fixing before
          {summary.invalid_rows === 1 ? " it goes" : " they go"} anywhere. Everything else is unaffected. Fix
          those lines in the sheet and upload it again.
        </Callout>
      )}

      {warnings > 0 && (
        <Callout variant="warning">
          {warnings} {warnings === 1 ? "row is" : "rows are"} flagged but not blocked. Read the note on each
          before confirming.
        </Callout>
      )}

      {summary.already_pushed_at !== null && (
        <Callout variant="warning">
          This exact file was already pushed into this Zoho organisation on {summary.already_pushed_at}.
          Pushing it again re-attempts every {copy.noun} on it. Zoho refuses a number it already holds, so
          what landed the first time comes back marked already there rather than doubled.
        </Callout>
      )}

      {/*
        Same books, a different client record of ours — legitimate (a re-run, a
        hand-over, a test) and worth one sentence before the push rather than a
        screen of "already exists" after it.
      */}
      {summary.already_pushed_at === null && summary.org_pushed_at !== null && (
        <Callout variant="warning">
          Another client record has already pushed {copy.nounPlural} into this same Zoho organisation, on{" "}
          {summary.org_pushed_at}. Anything on this sheet that is already over there comes back marked
          already there — check that this is the migration you mean to be running.
        </Callout>
      )}
    </div>
  );
}

/**
 * What the push actually did.
 *
 * Progress and result are the same component because they are the same numbers.
 * A separate progress bar that vanishes on completion means the CA watching it
 * finish sees the answer replaced at the exact moment it becomes worth reading.
 */
function PushOutcome({
  summary,
  push,
  pushing,
  toPush,
  noun,
  nounPlural,
}: {
  summary: OutstandingImportDTO;
  push: OutstandingPushDTO | null;
  pushing: boolean;
  toPush: number;
  noun: string;
  nounPlural: string;
}) {
  const settled =
    summary.created_rows + summary.updated_rows + summary.failed_rows + summary.duplicate_rows;
  const sent = summary.created_rows + summary.updated_rows;
  const done = summary.status === "done";

  return (
    <div className="flex flex-col gap-3">
      <div className="grid grid-cols-4 gap-2">
        <Tile label={ROW_STATUS_LABEL.created} count={summary.created_rows} tone="new" />
        <Tile label={ROW_STATUS_LABEL.updated} count={summary.updated_rows} tone="update" />
        <Tile label={ROW_STATUS_LABEL.duplicate} count={summary.duplicate_rows} tone="unchanged" />
        <Tile label={ROW_STATUS_LABEL.failed} count={summary.failed_rows} tone="invalid" />
      </div>

      {/*
        A count rather than a bar. The total is known and small, and "31 of 38"
        answers "how much longer" as well as a bar does while also surviving the
        push being paused, which a bar animating to nowhere does not.
      */}
      <p className="text-sm text-muted-foreground">
        {pushing
          ? `Sending… ${settled} of ${toPush} done.`
          : done
            ? // Refused rows are settled but not sent, and "6 of 6 sent" over a
              // list of 6 refusals is the counter lying about the one thing it
              // exists to say.
              `Finished. ${sent} of ${toPush} ${toPush === 1 ? noun : nounPlural} sent to Zoho.`
            : `Stopped at ${settled} of ${toPush}. Nothing further has been sent.`}
      </p>

      {push?.halted_reason != null && <Callout variant="warning">{push.halted_reason}</Callout>}

      {/*
        Two different pieces of news, and the old copy told the first one in
        both cases — "the documents already sent are in Zoho" over a push that
        had sent nothing at all.
      */}
      {!pushing && !done && push?.halted !== true && (
        <Callout variant="info">
          {sent === 0
            ? `This push did not finish, and no ${nounPlural} have reached Zoho yet. Continuing carries on from where it stopped.`
            : `This push did not finish. The ${nounPlural} already sent are in Zoho; continuing picks up from where it stopped rather than starting again.`}
        </Callout>
      )}

      {done && summary.duplicate_rows > 0 && (
        <Callout variant="info">
          {summary.duplicate_rows} {summary.duplicate_rows === 1 ? `${noun} was` : `${nounPlural} were`}{" "}
          already in Zoho Books under the same{" "}
          {summary.duplicate_rows === 1 ? "number" : "numbers"}, so nothing was sent for{" "}
          {summary.duplicate_rows === 1 ? "it" : "them"} and nothing was doubled. They are matched to the
          documents that were already there and count towards the totals above the sheet.
        </Callout>
      )}

      {done && summary.failed_rows > 0 && (
        <Callout variant="warning">
          Zoho refused {summary.failed_rows} {summary.failed_rows === 1 ? "row" : "rows"}. Each one
          carries its reason below. Everything else is in the client's books.
        </Callout>
      )}
    </div>
  );
}
